import type { AppNotification } from '../../api/monitor';
import { ErrorCard, LoadingCard } from '../../components/ui/ScreenFallback';
import type { NotificationsController } from '../../hooks/useNotifications';
import { NotificationRow } from './NotificationRow';

/**
 * Tela de Notificações: tudo o que aconteceu em segundo plano, do mais recente
 * para o mais antigo.
 *
 * O controller vem do App, e não nasce aqui: é o mesmo estado do sino no
 * cabeçalho — dois controllers dariam dois contadores discordando entre si.
 */
export function NotificationsScreen({ controller }: { controller: NotificationsController }) {
  const state = controller.state;

  if (controller.loading && !state) {
    return <LoadingCard message="Carregando as notificações…" />;
  }

  if (!state) {
    return (
      <ErrorCard
        title="Não foi possível carregar as notificações."
        detail={controller.error}
        onRetry={() => void controller.reload()}
      />
    );
  }

  const handleRead = (notification: AppNotification) => {
    if (!notification.read) void controller.markRead(notification.id);
  };

  return (
    <>
      <div className="mb-2.5 flex items-center gap-2">
        <div className="text-ink-400 flex-1 text-[11.5px]">
          {state.unread > 0
            ? `${state.unread} não lida(s) de ${state.notifications.length}`
            : `${state.notifications.length} aviso(s)`}
        </div>

        {state.unread > 0 ? (
          <button
            type="button"
            onClick={() => void controller.markSeen()}
            className="text-navy-800 text-[12.5px] font-extrabold hover:underline"
          >
            Marcar todas como lidas
          </button>
        ) : null}
      </div>

      {controller.error ? (
        <div className="border-danger-200 bg-danger-100 text-danger-600 mb-3 rounded-xl border px-4 py-3 text-[12.5px] font-semibold">
          {controller.error}
        </div>
      ) : null}

      <div className="border-line-200 overflow-hidden rounded-[14px] border bg-white">
        {state.notifications.length === 0 ? (
          <div className="text-ink-600 p-7 text-center text-[13.5px]">
            Nada aconteceu por aqui ainda. Rodadas de serviço, sincronizações de linha e
            relatórios prontos aparecem nesta lista.
          </div>
        ) : (
          state.notifications.map((notification) => (
            <NotificationRow
              key={notification.id}
              notification={notification}
              onRead={handleRead}
            />
          ))
        )}
      </div>
    </>
  );
}
